import type { SimpleLatLng } from "../Core";
import type { DataContextType } from "../contexts/DataContext";

export interface FramingCheckOptions {
  center: SimpleLatLng;
  zoom: number;
  frameWidth?: number;
  frameHeight?: number;
  points: SimpleLatLng[];
}

export type FramingIssueType =
  | "out_of_bounds"
  | "too_zoomed_out"
  | "off_center";

export interface FramingCheckResult {
  isValid: boolean;
  issueType?: FramingIssueType;
  title?: string;
  warningMessage?: string;
  bounds?: { minX: number; maxX: number; minY: number; maxY: number };
}

/**
 * Projects a latitude/longitude point to pixel coordinates in a frame
 * of size frameWidth x frameHeight centered at `center` at the given `zoom`.
 * Uses standard Web Mercator projection.
 */
export function projectLatLngToPixel(
  latLng: SimpleLatLng,
  center: SimpleLatLng,
  zoom: number,
  frameWidth = 720,
  frameHeight = 720,
): { x: number; y: number } {
  const scale = 256 * Math.pow(2, zoom);

  const sinY = Math.sin((latLng.lat * Math.PI) / 180);
  const clampedSinY = Math.min(Math.max(sinY, -0.9999), 0.9999);
  const worldX = (latLng.lng + 180) / 360;
  const worldY =
    0.5 - Math.log((1 + clampedSinY) / (1 - clampedSinY)) / (4 * Math.PI);

  const sinCenterY = Math.sin((center.lat * Math.PI) / 180);
  const clampedSinCenterY = Math.min(Math.max(sinCenterY, -0.9999), 0.9999);
  const centerWorldX = (center.lng + 180) / 360;
  const centerWorldY =
    0.5 -
    Math.log((1 + clampedSinCenterY) / (1 - clampedSinCenterY)) / (4 * Math.PI);

  const x = frameWidth / 2 + (worldX - centerWorldX) * scale;
  const y = frameHeight / 2 + (worldY - centerWorldY) * scale;

  return { x, y };
}

/**
 * Checks whether the site features fit cleanly within the export framing square (720x720).
 * Detects:
 * 1. Out of bounds (too zoomed in): features extend past the edges and will be cut off.
 * 2. Off center: features would fit, but are panned out of the frame.
 * 3. Too zoomed out: features occupy a tiny fraction of the square, making the site map hard to read in the field.
 */
export function checkSiteMapFraming({
  center,
  zoom,
  frameWidth = 720,
  frameHeight = 720,
  points,
}: FramingCheckOptions): FramingCheckResult {
  if (!points || points.length === 0 || !center || typeof zoom !== "number") {
    return { isValid: true };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const pt of points) {
    const { x, y } = projectLatLngToPixel(
      pt,
      center,
      zoom,
      frameWidth,
      frameHeight,
    );
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  const bounds = { minX, maxX, minY, maxY };
  const tolerance = 15; // 15px margin of leniency around border

  const spanX = maxX - minX;
  const spanY = maxY - minY;

  const isCutOff =
    minX < -tolerance ||
    maxX > frameWidth + tolerance ||
    minY < -tolerance ||
    maxY > frameHeight + tolerance;

  if (isCutOff) {
    if (spanX > frameWidth + tolerance || spanY > frameHeight + tolerance) {
      return {
        isValid: false,
        issueType: "out_of_bounds",
        title: "Site Map Too Zoomed In",
        warningMessage:
          "Some site map features (parking lots, boundary, or markers) extend beyond the red export frame and will be cut off in the exported image. Consider zooming out slightly.",
        bounds,
      };
    } else {
      return {
        isValid: false,
        issueType: "off_center",
        title: "Site Map Off-Center",
        warningMessage:
          "Some site map features are outside the red export frame because the map is shifted. Drag the map to center your property inside the red frame.",
        bounds,
      };
    }
  }

  // Too zoomed out: features span less than 25% of the frame in both dimensions (only evaluated when multiple features exist)
  if (points.length >= 2 && spanX < 180 && spanY < 180) {
    return {
      isValid: false,
      issueType: "too_zoomed_out",
      title: "Site Map Zoomed Very Far Out",
      warningMessage:
        "The site map features only occupy a small part of the red frame and will appear very small when viewed in the field. Consider zooming in to fill the frame.",
      bounds,
    };
  }

  return { isValid: true, bounds };
}

export type SiteMapFeaturePointData = Pick<
  DataContextType,
  "surfaceFeatures" | "boundary" | "hazardMarkers" | "labelMarkers" | "arrows"
>;

/**
 * Extracts coordinate points from all visible features in the site map data.
 */
export function extractSiteMapPoints({
  surfaceFeatures,
  boundary,
  hazardMarkers,
  labelMarkers,
  arrows,
}: SiteMapFeaturePointData): SimpleLatLng[] {
  const points: SimpleLatLng[] = [];

  // Holes sit inside the outer ring, so the outer ring is enough for framing
  for (const surface of surfaceFeatures) {
    for (const { lat, lng } of surface.coordinates[0] ?? []) {
      points.push({ lat, lng });
    }
  }

  // Boundary and arrow coords are [lat, lng] pairs
  for (const [lat, lng] of boundary?.coords ?? []) {
    points.push({ lat, lng });
  }

  for (const { lat, lng } of [...hazardMarkers, ...labelMarkers]) {
    points.push({ lat, lng });
  }

  for (const arrow of arrows) {
    for (const [lat, lng] of arrow.coords) {
      points.push({ lat, lng });
    }
  }

  return points;
}
